import { IParseExportPaymentsResponse } from "@shared/lib/types/briefCampains";
import { Button, Modal } from "@shared/ui";
import { useEffect, useState } from "react";
import { splitParseExportRows } from "../../lib/parseExportRows";
import { PayoutParsePreviewList } from "../PayoutParsePreviewList";
import styles from "./styles.module.scss";

type ParsePreviewTab = "success" | "failed" | "cannotImport";

type PayoutParsePreviewModalProps = {
  open: boolean;
  fileName?: string;
  rows: IParseExportPaymentsResponse[];
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const TAB_META: Record<ParsePreviewTab, { sectionTitle: string }> = {
  success: {
    sectionTitle: "Payment done successfully",
  },
  failed: {
    sectionTitle: "Failed",
  },
  cannotImport: {
    sectionTitle: "Could not import",
  },
};

export function PayoutParsePreviewModal({
  open,
  fileName,
  rows,
  isSubmitting,
  onClose,
  onConfirm,
}: PayoutParsePreviewModalProps) {
  const buckets = splitParseExportRows(rows);
  const [activeTab, setActiveTab] = useState<ParsePreviewTab>("success");
  const actionableCount = buckets.success.length + buckets.failed.length;
  const activeRows = buckets[activeTab];

  useEffect(() => {
    if (!open) return;
    if (buckets.success.length > 0) {
      setActiveTab("success");
      return;
    }
    if (buckets.failed.length > 0) {
      setActiveTab("failed");
      return;
    }
    setActiveTab("cannotImport");
  }, [
    open,
    buckets.success.length,
    buckets.failed.length,
    buckets.cannotImport.length,
  ]);

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={560}
      footer={null}
      closable={false}
      className={styles.modal}
    >
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Bank status sheet</h3>
          {fileName ? <p className={styles.fileName}>{fileName}</p> : null}
        </div>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          ×
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.summaryGrid}>
          <button
            type="button"
            className={`${styles.summaryCard} ${styles.successCard} ${
              activeTab === "success" ? styles.summaryCardActive : ""
            }`}
            onClick={() => setActiveTab("success")}
          >
            <div className={`${styles.summaryLabel} ${styles.successLabel}`}>
              Success
            </div>
            <div className={`${styles.summaryCount} ${styles.successCount}`}>
              {buckets.success.length}
            </div>
          </button>
          <button
            type="button"
            className={`${styles.summaryCard} ${styles.failedCard} ${
              activeTab === "failed" ? styles.summaryCardActive : ""
            }`}
            onClick={() => setActiveTab("failed")}
          >
            <div className={`${styles.summaryLabel} ${styles.failedLabel}`}>
              Failed
            </div>
            <div className={`${styles.summaryCount} ${styles.failedCount}`}>
              {buckets.failed.length}
            </div>
          </button>
          <button
            type="button"
            className={`${styles.summaryCard} ${styles.cannotImportCard} ${
              activeTab === "cannotImport" ? styles.summaryCardActive : ""
            }`}
            onClick={() => setActiveTab("cannotImport")}
          >
            <div
              className={`${styles.summaryLabel} ${styles.cannotImportLabel}`}
            >
              Could not import
            </div>
            <div
              className={`${styles.summaryCount} ${styles.cannotImportCount}`}
            >
              {buckets.cannotImport.length}
            </div>
          </button>
        </div>

        <div className={styles.section}>
          <div
            className={`${styles.sectionTitle} ${
              activeTab === "success"
                ? styles.successLabel
                : activeTab === "failed"
                  ? styles.failedLabel
                  : styles.cannotImportLabel
            }`}
          >
            {TAB_META[activeTab].sectionTitle}
          </div>
          <PayoutParsePreviewList rows={activeRows} kind={activeTab} />
        </div>
      </div>

      <div className={styles.footer}>
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          loading={isSubmitting}
          disabled={actionableCount === 0}
          onClick={onConfirm}
        >
          Apply statuses
        </Button>
      </div>
    </Modal>
  );
}
